import { useState, useEffect } from "react";
import { useOrganization } from "@/context/OrganizationContext";
import { useAuth } from "@/context/AuthContext";
import { 
  Component, 
  ComponentType, 
  SortColumn, 
  SortDirection, 
  fetchComponentTypes,
  createComponent, 
  updateComponent, 
  deleteComponent 
} from "@/utils/componentService";
import { fetchComponents } from "@/utils/services/componentService";
import { ensureComponentColumns } from "@/utils/services/databaseMigrationService";

export function useComponentManagement() {
  const { currentOrganization } = useOrganization();
  const { subscriptionData } = useAuth();
  const [components, setComponents] = useState<Component[]>([]);
  const [componentTypes, setComponentTypes] = useState<ComponentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState<SortColumn>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentComponent, setCurrentComponent] = useState<Component | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadComponentTypes();
    // Check and ensure database columns exist
    ensureComponentColumns();
  }, [subscriptionData?.subscription_id]);

  useEffect(() => {
    if (currentOrganization?.id) {
      loadComponents();
    }
  }, [currentOrganization, sortColumn, sortDirection, filter, typeFilter]);

  const loadComponentTypes = async () => {
    try {
      const types = await fetchComponentTypes(subscriptionData?.subscription_id);
      setComponentTypes(types);
    } catch (error) {
      console.error("Erro ao carregar tipos de componentes:", error);
    }
  };

  const loadComponents = async () => {
    setLoading(true);
    try {
      if (currentOrganization?.id) {
        const data = await fetchComponents(
          currentOrganization.id,
          sortColumn,
          sortDirection,
          filter,
          typeFilter
        );
        setComponents(data);
      }
    } catch (error) {
      console.error("Erro ao carregar componentes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleEdit = (component: Component | null) => {
    setCurrentComponent(component);
    setEditDialogOpen(component !== null);
  };

  const handleDelete = (component: Component | null) => {
    setCurrentComponent(component);
    setDeleteDialogOpen(component !== null);
  };

  const handleUpdateComponent = async (name: string, componentTypeId: string, repoType: string, repoName: string, repoUrl: string, mainUrl: string, logsUrl: string, dashUrl: string, swaggerUrl: string) => {
    if (!currentComponent) return;
    
    setIsSaving(true);
    try {
      const success = await updateComponent(currentComponent.id, name, componentTypeId, repoType, repoName, repoUrl, mainUrl, logsUrl, dashUrl, swaggerUrl);
      
      if (success) {
        await loadComponents();
        setEditDialogOpen(false);
      }
    } catch (error) {
      console.error("Erro ao atualizar componente:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateComponent = async (name: string, componentTypeId: string, repoType: string, repoName: string, repoUrl: string, mainUrl: string, logsUrl: string, dashUrl: string, swaggerUrl: string) => {
    if (!currentOrganization?.id) return;
    
    setIsSaving(true);
    try {
      const result = await createComponent(name, componentTypeId, currentOrganization.id, repoType, repoName, repoUrl, mainUrl, logsUrl, dashUrl, swaggerUrl);
      
      if (result.success) {
        await loadComponents();
        setCreateDialogOpen(false);
      }
    } catch (error) {
      console.error("Erro ao criar componente:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!currentComponent) return;
    
    setIsDeleting(true);
    try {
      const success = await deleteComponent(currentComponent.id);
      
      if (success) {
        await loadComponents();
        setDeleteDialogOpen(false);
      }
    } catch (error) {
      console.error("Erro ao excluir componente:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    components,
    componentTypes,
    loading,
    sortColumn,
    sortDirection,
    filter,
    typeFilter,
    createDialogOpen,
    editDialogOpen,
    deleteDialogOpen,
    currentComponent,
    isSaving,
    isDeleting,
    setFilter,
    setTypeFilter,
    setCreateDialogOpen,
    handleSort,
    handleEdit,
    handleDelete,
    handleUpdateComponent,
    handleCreateComponent,
    handleConfirmDelete
  };
}
